// components/Filters/FilterPanel.jsx
/**
 * Componente FilterPanel - Painel de filtros para exibição e manipulação de dados
 * 
 * Este arquivo contém um componente React que implementa uma interface de filtros
 * utilizando componentes do Material UI. O painel permite filtrar dados por período,
 * situação, WhatsAppId e data específica.
 */

// Importação dos componentes necessários do Material UI
import { Box, TextField, MenuItem, Button } from '@mui/material';

/**
 * Opções disponíveis para o filtro de período
 * Contém as diferentes opções de intervalo de tempo que o usuário pode selecionar
 */
const dateOptions = [
  "Todos",
  "Mês atual",
  "Últimos 7 dias",
  "Últimos 30 dias",
  "Últimos 3 meses",
  "Últimos 6 meses",
  "Últimos 12 meses"
];

/**
 * Opções disponíveis para o filtro de situação
 * Define os possíveis estados que podem ser filtrados
 */
const situacaoOptions = ["Todos", "Em Aberto", "Concluído"];

/**
 * Componente FilterPanel
 * 
 * @param {Object} props - Propriedades do componente
 * @param {string} props.dateFilter - Valor atual do filtro de período
 * @param {Function} props.setDateFilter - Função para atualizar o filtro de período
 * @param {string} props.situacaoFilter - Valor atual do filtro de situação
 * @param {Function} props.setSituacaoFilter - Função para atualizar o filtro de situação
 * @param {string} props.whatsAppIdFilter - Valor atual do filtro de WhatsAppId
 * @param {Function} props.setWhatsAppIdFilter - Função para atualizar o filtro de WhatsAppId
 * @param {string} props.dataFilter - Valor atual do filtro de data específica
 * @param {Function} props.setDataFilter - Função para atualizar o filtro de data
 * @param {Function} props.onClearFilters - Função para limpar todos os filtros
 * @param {boolean} props.hideSituacaoFilter - Flag para ocultar o filtro de situação (opcional)
 * @param {string} props.title - Título personalizado para o painel (opcional)
 * @returns {JSX.Element} Painel de filtros renderizado
 */
export default function FilterPanel({
  dateFilter,
  setDateFilter,
  situacaoFilter,
  setSituacaoFilter,
  whatsAppIdFilter,
  setWhatsAppIdFilter,
  dataFilter,
  setDataFilter,
  onClearFilters,
  hideSituacaoFilter = false,
  title = "Filtros de Dados"
}) {
  // Estilo comum para os campos de input
  const inputStyle = {
    bgcolor: 'rgba(255,255,255,0.95)',
    borderRadius: 2,
    '& .MuiInputLabel-root': {
      color: 'rgba(0,0,0,0.7)', // mantém a label visível
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: 1.5,
      '&.Mui-focused fieldset': {
        borderColor: '#667eea',
      },
    },
  };

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: { xs: 2, md: 3 },
        p: 4,
        mb: 2,
        boxShadow: {
          xs: '0 2px 10px rgba(0,0,0,0.08)',
          md: '0 4px 20px rgba(0,0,0,0.08)'
        },
        mx: 'auto', // Centraliza horizontalmente
        maxWidth: '100%', // Define largura máxima (opcional)
        border: '1px solid rgba(0,0,0,0.05)',
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1 }}>

        {/* Container dos filtros com layout responsivo */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: hideSituacaoFilter ? 'repeat(4, 1fr)' : 'repeat(5, 1fr)'
            },
            gap: { xs: 2, md: 3 },
            alignItems: 'start',
          }}
        >
          {/* Filtro de período */}
          <TextField
            select
            label="Filtrar por"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            sx={inputStyle}
          >
            {dateOptions.map(option => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </TextField>

          {/* Filtro de situação - Só exibe se hideSituacaoFilter for false */}
          {!hideSituacaoFilter && (
            <TextField
              select
              label="Filtrar por situação"
              value={situacaoFilter}
              onChange={(e) => setSituacaoFilter(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              sx={inputStyle}
            >
              {situacaoOptions.map(option => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
          )}

          {/* Filtro de WhatsAppId - Input de texto */}
          <TextField
            label="WhatsAppId"
            value={whatsAppIdFilter}
            onChange={(e) => setWhatsAppIdFilter(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{
              shrink: whatsAppIdFilter ? true : false
            }}
            sx={{
              bgcolor: 'rgba(255,255,255,0.95)',
              borderRadius: 1.5,
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                '&.Mui-focused fieldset': {
                  borderColor: '#667eea',
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                opacity: 1,
              },
            }}
          />

          {/* Filtro de data específica - Input de data */}
          <TextField
            label=""
            type="date"
            value={dataFilter}
            onChange={(e) => setDataFilter(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={inputStyle}
          />

          {/* Botão para limpar todos os filtros */}
          <Button
            variant="contained"
            onClick={onClearFilters}
            fullWidth
            sx={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.25) 100%)',
              color: '#1a1a1a',
              fontWeight: 600,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '0.875rem',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden',
              '&:before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                transition: 'left 0.6s ease',
              },
              '&:hover': {
                background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.35) 100%)',
                boxShadow: '0 6px 20px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.6)',
                transform: 'translateY(-1px)',
                '&:before': {
                  left: '100%',
                },
              },
              '&:active': {
                transform: 'translateY(0px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.4)',
              },
            }}
          >
            Limpar
          </Button>
        </Box>
      </Box>
    </Box>
  );
}